import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  FormFeedback,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { useSelector } from "react-redux";
import AiChatLogo from "../../assets/images/ai-chat.png";
import { postDataAPI } from "../../helpers/phpbackend_helper";
import { AI_CHAT } from "../../helpers/data_helper";
import "./AiChat.css";
import RecordDescriptionModal from "../Coaching/RecordDescriptionModal";

const THINKING_PHRASES = [
  "Thinking...",
  "Understanding your question...",
  "Figuring out what you need...",
  "Checking the details...",
  "Putting it together...",
  "Almost there...",
];

const THINKING_ROTATE_MS = 2500;

const OPENAI_META_KEYS = [
  "conversation_id",
  "user_turn_id",
  "display_message",
  "openai_request_messages",
  "openai_response_raw",
];

const extractIntentForDisplay = (data) => {
  if (!data || typeof data !== "object") {
    return data;
  }
  const intent = { ...data };
  OPENAI_META_KEYS.forEach((key) => {
    delete intent[key];
  });
  return intent;
};

const normalizeMessageText = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
};

const formatContentForEdit = (content) => {
  const normalized = normalizeMessageText(content);
  if (!normalized) {
    return "";
  }

  try {
    return JSON.stringify(JSON.parse(normalized), null, 2);
  } catch {
    return normalized;
  }
};

const PAYLOAD_TABS = [
  { id: "system", label: "System prompt" },
  { id: "history", label: "Prior conversation" },
  { id: "user", label: "User prompt" },
  { id: "response", label: "OpenAI response" },
];

const formatMessagesForDisplay = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return "";
  }

  return JSON.stringify(
    {
      messages: messages.map((msg) => {
        const normalized = normalizeMessageText(msg.content);
        try {
          return {
            role: msg.role,
            content: JSON.parse(normalized),
          };
        } catch {
          return {
            role: msg.role,
            content: normalized,
          };
        }
      }),
    },
    null,
    2,
  );
};

const formatOpenAiResponseForDisplay = (raw) => {
  if (raw === null || raw === undefined || raw === "") {
    return "";
  }

  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    const content = parsed?.choices?.[0]?.message?.content;

    if (typeof content === "string" && content.trim() !== "") {
      try {
        return JSON.stringify(JSON.parse(content), null, 2);
      } catch {
        return content;
      }
    }

    return JSON.stringify(parsed, null, 2);
  } catch {
    return normalizeMessageText(raw);
  }
};

const extractHistoryMessages = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return [];
  }

  const normalized = messages.map((msg) => ({
    role: msg.role,
    content: normalizeMessageText(msg.content),
  }));
  const systemIndex = findMessageIndex(normalized, "system");
  const userIndex = findMessageIndex(normalized, "user", true);

  if (systemIndex < 0 || userIndex < 0) {
    return [];
  }

  return normalized.filter((_, index) => index !== systemIndex && index !== userIndex);
};

const findMessageIndex = (messages, role, fromEnd = false) => {
  if (!Array.isArray(messages)) {
    return -1;
  }

  if (fromEnd) {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === role) {
        return i;
      }
    }
    return -1;
  }

  return messages.findIndex((msg) => msg?.role === role);
};

const buildEditablePayloadState = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return {
      allMessages: [],
      systemContent: "",
      userContent: "",
      historyContent: "",
      openaiResponseContent: "",
      historyCount: 0,
    };
  }

  const systemIndex = findMessageIndex(messages, "system");
  const userIndex = findMessageIndex(messages, "user", true);
  const historyMessages = extractHistoryMessages(messages);

  return {
    allMessages: messages.map((msg) => ({
      role: msg.role,
      content: normalizeMessageText(msg.content),
    })),
    systemContent:
      systemIndex >= 0 ? formatContentForEdit(messages[systemIndex].content) : "",
    userContent:
      userIndex >= 0 ? formatContentForEdit(messages[userIndex].content) : "",
    historyContent: formatMessagesForDisplay(historyMessages),
    openaiResponseContent: "",
    historyCount: historyMessages.length,
  };
};

const rebuildOpenAiRequestMessages = (allMessages, systemContent, userContent) => {
  const messages = allMessages.map((msg) => ({
    role: msg.role,
    content: normalizeMessageText(msg.content),
  }));

  const systemIndex = findMessageIndex(messages, "system");
  if (systemIndex >= 0) {
    messages[systemIndex] = {
      role: "system",
      content: normalizeMessageText(systemContent),
    };
  }

  const userIndex = findMessageIndex(messages, "user", true);
  if (userIndex >= 0) {
    messages[userIndex] = {
      role: "user",
      content: normalizeMessageText(userContent),
    };
  }

  return messages.filter((msg) => msg.content.trim() !== "");
};

const extractDisplayMessageFromPayload = (messages) => {
  const userIndex = findMessageIndex(messages, "user", true);
  if (userIndex < 0) {
    return null;
  }

  const content = normalizeMessageText(messages[userIndex].content);
  try {
    const parsed = JSON.parse(content);
    if (parsed?.current_message) {
      return String(parsed.current_message).trim();
    }
    if (parsed?.message) {
      return String(parsed.message).trim();
    }
  } catch {
    // plain text user prompt
  }

  return content.trim() || null;
};

const formatBotResponse = (data) => {
  if (!data || typeof data !== "object") {
    return {
      text: String(data || "No response returned."),
      raw: null,
      showJson: false,
    };
  }

  const intentOnly = extractIntentForDisplay(data);

  if (intentOnly.status === "valid") {
    return {
      text: JSON.stringify(intentOnly, null, 2),
      raw: intentOnly,
      showJson: true,
    };
  }

  const message =
    intentOnly.clarification_message ||
    intentOnly.message ||
    intentOnly.summary ||
    "Could you share a bit more detail so I can help?";

  return {
    text: message,
    raw: null,
    showJson: false,
  };
};

const OpenAiRequestTabbedPanel = ({
  activeTab,
  onTabChange,
  systemContent,
  userContent,
  historyContent,
  openaiResponseContent,
  validationError,
  onSystemChange,
  onUserChange,
  isEditMode,
}) => {
  const renderTextarea = (id, value, onChange, editable, rows = 16) => (
    <Input
      id={id}
      type="textarea"
      className="ai-chat-payload-textarea"
      rows={rows}
      value={value}
      onChange={onChange}
      readOnly={!editable}
      invalid={editable && !!validationError}
    />
  );

  const renderReadonlyBlock = (value, emptyText) => (
    <pre className="ai-chat-payload-readonly">
      {value?.trim() ? value : emptyText}
    </pre>
  );

  return (
    <div className="ai-chat-payload-editor">
      <Nav tabs className="ai-chat-payload-tabs">
        {PAYLOAD_TABS.map((tab) => (
          <NavItem key={tab.id}>
            <NavLink
              className={classnames({ active: activeTab === tab.id })}
              onClick={() => onTabChange(tab.id)}
              style={{ cursor: "pointer" }}
            >
              {tab.label}
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      <TabContent activeTab={activeTab} className="ai-chat-payload-tab-content">
        <TabPane tabId="system">
          <div className="ai-chat-payload-field">
            <Label for="ai-chat-system-prompt" className="ai-chat-payload-label">
              System prompt
            </Label>
            {renderTextarea(
              "ai-chat-system-prompt",
              systemContent,
              (e) => onSystemChange(e.target.value),
              isEditMode,
              18,
            )}
          </div>
        </TabPane>

        <TabPane tabId="history">
          <div className="ai-chat-payload-field">
            <Label className="ai-chat-payload-label">Prior conversation</Label>
            {renderReadonlyBlock(historyContent, "No prior conversation messages.")}
          </div>
        </TabPane>

        <TabPane tabId="user">
          <div className="ai-chat-payload-field">
            <Label for="ai-chat-user-prompt" className="ai-chat-payload-label">
              User prompt
            </Label>
            {renderTextarea(
              "ai-chat-user-prompt",
              userContent,
              (e) => onUserChange(e.target.value),
              isEditMode,
              18,
            )}
            {isEditMode && validationError ? (
              <FormFeedback className="d-block">{validationError}</FormFeedback>
            ) : null}
          </div>
        </TabPane>

        <TabPane tabId="response">
          <div className="ai-chat-payload-field">
            <Label className="ai-chat-payload-label">OpenAI response</Label>
            {renderReadonlyBlock(openaiResponseContent, "No OpenAI response available.")}
          </div>
        </TabPane>
      </TabContent>
    </div>
  );
};

const AiChat = () => {
  const { userInfo } = useSelector((state) => ({
    userInfo: state.UserOutletGroup.userInfo,
  }));

  const firstName = useMemo(
    () => userInfo?.first_name || userInfo?.firstName || "there",
    [userInfo],
  );

  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const thinkingIntervalRef = useRef(null);
  const thinkingPhraseIndexRef = useRef(0);
  const messagesContainerRef = useRef(null);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [payloadModal, setPayloadModal] = useState({
    open: false,
    mode: "view",
    activeTab: "system",
    userMessageId: null,
    userTurnId: null,
    allMessages: [],
    systemContent: "",
    userContent: "",
    historyContent: "",
    openaiResponseContent: "",
    historyCount: 0,
    validationError: "",
  });

  useEffect(() => {
    if (messages.length > 0 && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages]);

  useEffect(() => {
    if (!loading) {
      if (thinkingIntervalRef.current) {
        clearInterval(thinkingIntervalRef.current);
        thinkingIntervalRef.current = null;
      }
      return undefined;
    }

    thinkingPhraseIndexRef.current = 0;
    thinkingIntervalRef.current = setInterval(() => {
      thinkingPhraseIndexRef.current =
        (thinkingPhraseIndexRef.current + 1) % THINKING_PHRASES.length;
      const phrase = THINKING_PHRASES[thinkingPhraseIndexRef.current];
      setMessages((prev) =>
        prev.map((msg) => (msg.isThinking ? { ...msg, text: phrase } : msg)),
      );
    }, THINKING_ROTATE_MS);

    return () => {
      if (thinkingIntervalRef.current) {
        clearInterval(thinkingIntervalRef.current);
        thinkingIntervalRef.current = null;
      }
    };
  }, [loading]);

  const closePayloadModal = () => {
    setPayloadModal({
      open: false,
      mode: "view",
      activeTab: "system",
      userMessageId: null,
      userTurnId: null,
      allMessages: [],
      systemContent: "",
      userContent: "",
      historyContent: "",
      openaiResponseContent: "",
      historyCount: 0,
      validationError: "",
    });
  };

  const getLatestUserMessageId = () => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.sender === "user") {
        return messages[i].id;
      }
    }
    return null;
  };

  const openPayloadModal = (message) => {
    const editable = buildEditablePayloadState(message.openaiRequestMessages);
    const isLatestUserMessage = message.id === getLatestUserMessageId();
    setPayloadModal({
      open: true,
      mode: isLatestUserMessage ? "edit" : "view",
      activeTab: "system",
      userMessageId: message.id,
      userTurnId: message.userTurnId,
      ...editable,
      openaiResponseContent: formatOpenAiResponseForDisplay(message.openaiResponseRaw),
      validationError: "",
    });
  };

  const validatePayloadEditor = (systemContent, userContent) => {
    if (!systemContent.trim()) {
      return "System prompt is required.";
    }
    if (!userContent.trim()) {
      return "User prompt is required.";
    }
    return "";
  };

  const sendMessage = async (text) => {
    const payload = {
      serviceName: AI_CHAT.serviceName,
      op: AI_CHAT.op_chat,
      message: text,
    };

    if (conversationId) {
      payload.conversation_id = conversationId;
    }

    const response = await postDataAPI(payload);
    const responseData = response?.data ?? response?.Data ?? null;

    if (response.status !== "ok") {
      throw new Error(response.message || "AI chat request failed");
    }

    if (responseData?.conversation_id) {
      setConversationId(responseData.conversation_id);
    }

    return responseData;
  };

  const resendMessage = async ({ userTurnId, openaiRequestMessages }) => {
    const payload = {
      serviceName: AI_CHAT.serviceName,
      op: AI_CHAT.op_resend,
      conversation_id: conversationId,
      user_turn_id: userTurnId,
      openai_request_messages: openaiRequestMessages,
    };

    const response = await postDataAPI(payload);
    const responseData = response?.data ?? response?.Data ?? null;

    if (response.status !== "ok") {
      throw new Error(response.message || "AI chat resend failed");
    }

    if (responseData?.conversation_id) {
      setConversationId(responseData.conversation_id);
    }

    return responseData;
  };

  const applyBotResponseToMessages = (botMessageId, userMessageId, botResponse) => {
    const formatted = formatBotResponse(botResponse);
    const displayMessage =
      botResponse?.display_message || extractDisplayMessageFromPayload(botResponse?.openai_request_messages);

    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id === userMessageId) {
          return {
            ...msg,
            text: displayMessage || msg.text,
            userTurnId: botResponse?.user_turn_id ?? msg.userTurnId,
            openaiRequestMessages: botResponse?.openai_request_messages || msg.openaiRequestMessages,
            openaiResponseRaw: botResponse?.openai_response_raw ?? msg.openaiResponseRaw,
          };
        }
        if (msg.id === botMessageId) {
          return {
            ...msg,
            text: formatted.text,
            raw: formatted.raw,
            showJson: formatted.showJson,
            isThinking: false,
          };
        }
        return msg;
      }),
    );
  };

  const submitText = async (text) => {
    if (loading) return;
    const trimmed = (text || "").trim();
    if (!trimmed) return;

    const userMessage = {
      id: Date.now(),
      sender: "user",
      text: trimmed,
      userTurnId: null,
      openaiRequestMessages: null,
      openaiResponseRaw: null,
    };
    const botMessage = {
      id: Date.now() + 1,
      sender: "bot",
      text: THINKING_PHRASES[0],
      isThinking: true,
    };

    setMessages((prev) => [...prev, userMessage, botMessage]);
    setQuery("");
    setLoading(true);

    try {
      const botResponse = await sendMessage(trimmed);
      applyBotResponseToMessages(botMessage.id, userMessage.id, botResponse);
    } catch (error) {
      const errorText = error?.message || "Unable to get AI response.";
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botMessage.id
            ? { ...msg, text: errorText, isThinking: false, showJson: false, raw: null }
            : msg,
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendPayload = async () => {
    const validationError = validatePayloadEditor(
      payloadModal.systemContent,
      payloadModal.userContent,
    );
    if (validationError) {
      setPayloadModal((prev) => ({ ...prev, validationError }));
      return;
    }

    if (!conversationId || !payloadModal.userTurnId || !payloadModal.userMessageId) {
      setPayloadModal((prev) => ({
        ...prev,
        validationError: "This message cannot be resent yet. Send it once, then try again.",
      }));
      return;
    }

    const rebuiltMessages = rebuildOpenAiRequestMessages(
      payloadModal.allMessages,
      payloadModal.systemContent,
      payloadModal.userContent,
    );

    const userMessageId = payloadModal.userMessageId;
    const userTurnId = payloadModal.userTurnId;
    const userMessageIndex = messages.findIndex((msg) => msg.id === userMessageId);
    if (userMessageIndex < 0) {
      return;
    }

    const botMessage = messages[userMessageIndex + 1];
    if (!botMessage || botMessage.sender !== "bot") {
      return;
    }

    closePayloadModal();
    setLoading(true);

    setMessages((prev) => {
      const kept = prev.slice(0, userMessageIndex + 2);
      return kept.map((msg) =>
        msg.id === botMessage.id
          ? { ...msg, text: THINKING_PHRASES[0], isThinking: true, showJson: false, raw: null }
          : msg,
      );
    });

    try {
      const botResponse = await resendMessage({
        userTurnId,
        openaiRequestMessages: rebuiltMessages,
      });
      applyBotResponseToMessages(botMessage.id, userMessageId, botResponse);
    } catch (error) {
      const errorText = error?.message || "Unable to resend AI request.";
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botMessage.id
            ? { ...msg, text: errorText, isThinking: false, showJson: false, raw: null }
            : msg,
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (loading) return;
    submitText(query);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="justify-content-center align-items-center h-100">
          <Col lg={11} xl={10}>
            <Card className="ai-chat-card shadow-none border-0">
              <CardBody className="ai-chat-card-body">
                {messages.length === 0 ? (
                  <div className="ai-chat-empty-state">
                    <div className="ai-chat-brand">
                      <div className="ai-chat-logo">
                        <img src={AiChatLogo} alt="AI Chat" />
                      </div>
                    </div>
                    <h1 className="ai-chat-title mb-5 text-center">
                      Hey, {firstName}. Let's dive in?
                    </h1>

                    <form className="ai-chat-form" onSubmit={handleSubmit}>
                      <div className="ai-chat-input-group w-100 position-relative">
                        <Input
                          className="ai-chat-input"
                          type="text"
                          value={query}
                          placeholder="Write your message..."
                          onChange={(e) => setQuery(e.target.value)}
                        />
                        <Button
                          type="button"
                          className="ai-chat-mic-btn btn-icon"
                          onClick={() => setShowRecordModal(true)}
                          disabled={loading}
                        >
                          <i className="ri-mic-line fs-5" />
                        </Button>
                        <Button
                          type="submit"
                          className="ai-chat-button btn-icon"
                          disabled={loading}
                        >
                          {loading ? <Spinner size="sm" /> : <i className="ri-send-plane-line fs-5" />}
                        </Button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <div className="ai-chat-conversation">
                    <div className="ai-chat-dialogue">
                      <div ref={messagesContainerRef} className="ai-chat-messages">
                        {messages.map((message) => (
                          <div
                            key={message.id}
                            className={`ai-chat-message ${message.sender}`}
                          >
                            {message.sender === "user" &&
                              Array.isArray(message.openaiRequestMessages) &&
                              message.openaiRequestMessages.length > 0 && (
                                <button
                                  type="button"
                                  className="ai-chat-payload-btn"
                                  title={
                                    message.id === getLatestUserMessageId()
                                      ? "Edit and resend OpenAI request"
                                      : "View OpenAI request"
                                  }
                                  onClick={() => openPayloadModal(message)}
                                  disabled={loading}
                                >
                                  <i
                                    className={
                                      message.id === getLatestUserMessageId()
                                        ? "ri-edit-line"
                                        : "ri-add-line"
                                    }
                                  />
                                </button>
                              )}
                            <div
                              className={`ai-chat-bubble${
                                message.isThinking ? " ai-chat-bubble-thinking" : ""
                              }`}
                            >
                              {message.showJson && message.raw ? (
                                <pre className="ai-chat-json">{message.text}</pre>
                              ) : (
                                message.text
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="ai-chat-footer">
                      <form className="ai-chat-form" onSubmit={handleSubmit}>
                        <div className="ai-chat-input-group w-100 position-relative">
                          <Input
                            className="ai-chat-input"
                            type="text"
                            value={query}
                            placeholder="Write your message..."
                            onChange={(e) => setQuery(e.target.value)}
                          />
                          <Button
                            type="button"
                            className="ai-chat-mic-btn btn-icon"
                            onClick={() => setShowRecordModal(true)}
                            disabled={loading}
                          >
                            <i className="ri-mic-line fs-5" />
                          </Button>
                          <Button
                            type="submit"
                            className="ai-chat-button btn-icon"
                            disabled={loading}
                          >
                            {loading ? <Spinner size="sm" /> : <i className="ri-send-plane-line fs-5" />}
                          </Button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <RecordDescriptionModal
        isOpen={showRecordModal}
        onClose={() => setShowRecordModal(false)}
        onSave={(text) => {
          setShowRecordModal(false);
          submitText(text);
        }}
        dishName={"Chat"}
      />
      <Modal
        isOpen={payloadModal.open}
        toggle={closePayloadModal}
        size="lg"
        className="ai-chat-payload-modal"
      >
        <ModalHeader toggle={closePayloadModal}>
          {payloadModal.mode === "edit" ? "Edit OpenAI request" : "OpenAI request"}
        </ModalHeader>
        <ModalBody className="ai-chat-payload-modal-body">
          <OpenAiRequestTabbedPanel
            activeTab={payloadModal.activeTab}
            onTabChange={(tabId) =>
              setPayloadModal((prev) => ({
                ...prev,
                activeTab: tabId,
              }))
            }
            systemContent={payloadModal.systemContent}
            userContent={payloadModal.userContent}
            historyContent={payloadModal.historyContent}
            openaiResponseContent={payloadModal.openaiResponseContent}
            validationError={payloadModal.validationError}
            isEditMode={payloadModal.mode === "edit"}
            onSystemChange={(value) =>
              setPayloadModal((prev) => ({
                ...prev,
                systemContent: value,
                validationError: "",
              }))
            }
            onUserChange={(value) =>
              setPayloadModal((prev) => ({
                ...prev,
                userContent: value,
                validationError: "",
              }))
            }
          />
        </ModalBody>
        <ModalFooter className="ai-chat-payload-modal-footer">
          <Button color="light" onClick={closePayloadModal} disabled={loading}>
            {payloadModal.mode === "edit" ? "Cancel" : "Close"}
          </Button>
          {payloadModal.mode === "edit" ? (
            <Button color="primary" onClick={handleResendPayload} disabled={loading}>
              {loading ? <Spinner size="sm" className="me-2" /> : <i className="ri-send-plane-line me-1" />}
              Resend
            </Button>
          ) : null}
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default AiChat;
